import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { apiService } from '../services/api';
import { Cart } from '../types';
import './PaymentConfirmationPage.scss';

const PaymentConfirmationPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<any | null>(null);
  const [passes, setPasses] = useState<any[]>([]);
  const [email, setEmail] = useState<string>('');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(30);

  useEffect(() => {
    if (cartId) {
      loadCartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  // Countdown timer for redirect
  useEffect(() => {
    if (redirectUrl && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (redirectUrl && countdown === 0) {
      window.location.href = redirectUrl;
    }
  }, [redirectUrl, countdown]);

  const loadCartData = async () => {
    if (!cartId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch confirmation data for the specific cartId
      const data = await apiService.payment.getConfirmation(parseInt(cartId, 10));

      if (!data.success) {
        throw new Error(data.error || 'Failed to load confirmation');
      }

      // Normalize cart shape to what the UI expects
      const normalizedCart = {
        id: data.cart.id,
        total: data.cart.total,
        currency: typeof data.cart.currency === 'string' ? { code: data.cart.currency } : data.cart.currency,
        pgw: data.cart.pgw || null,
        created_at: data.cart.created_at,
      };

      setCart(normalizedCart);
      setPasses(data.passes || []);
      setEmail(data.email || '');

      // Optional: if backend provides event redirect URL in future, set it here
      // setRedirectUrl(data.redirect_url || null);
      
    } catch (error: any) {
      console.error('Error loading cart data:', error);
      const status = error?.response?.status;
      const message = error?.response?.data?.error || 'Failed to load confirmation details. Please try again.';
      setError(message);
      if (status === 410) {
        // Confirmation already viewed; redirect to home after short delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbol = currency === 'BDT' ? '৳' : currency === 'THB' ? '฿' : '$';
    return `${symbol}${amount.toFixed(2)}`;
  };

  const getItemTitle = (item: any) => {
    if (item.event_pass?.event?.title) {
      return item.event_pass.event.title;
    } else if (item.activity_booking?.activity_place?.name) {
      return item.activity_booking.activity_place.name;
    } else if (item.product_item?.product?.name) {
      return item.product_item.product.name;
    }
    return 'Ticket';
  };

  const getItemImage = (item: any) => {
    if (item.event_pass?.event?.banner_url) {
      return item.event_pass.event.banner_url;
    } else if (item.activity_booking?.activity_place?.image) {
      return item.activity_booking.activity_place.image;
    } else if (item.product_item?.product?.image) {
      return item.product_item.product.image;
    }
    return null;
  };

  const getPassDownloadLink = (pass: any) => {
    // Use the same base URL as the API service
    return `http://localhost:8000/api/passes/${pass.id}/download/`;
  };

  const handleGoogleWalletClick = async (pass: any, e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const result = await apiService.passes.download(pass.id, 'google-wallet');
      if (result.success && result.redirect_url) {
        window.open(result.redirect_url, '_blank', 'noopener,noreferrer');
      } else {
        console.error('Failed to get Google Wallet link:', result.error);
        alert('Failed to open Google Wallet. Please try again.');
      }
    } catch (error) {
      console.error('Error getting Google Wallet link:', error);
      alert('Failed to open Google Wallet. Please try again.');
    }
  };

  const handleDownloadClick = () => {
    // Add 30 seconds to countdown when download is clicked
    setCountdown(prev => prev + 30);
  };

  if (loading) {
    return (
      <Container className="payment-confirmation-page">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="glass">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5>Loading Confirmation...</h5>
                <p className="text-muted">Please wait while we load your order details.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="payment-confirmation-page">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="glass">
              <Card.Body className="text-center py-4">
                <Alert variant="danger">
                  {error}
                </Alert>
                <Button variant="primary" onClick={() => navigate('/cart')}>
                  Back to Cart
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!cart) {
    return (
      <Container className="payment-confirmation-page">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="glass">
              <Card.Body className="text-center py-4">
                <Alert variant="warning">
                  No order found with this ID.
                </Alert>
                <Button variant="primary" onClick={() => navigate('/')}>
                  Go Home
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div className="payment-confirmation-page">
      {/* Hero Section */}
      <div className="confirmation-hero">
        <h3 className="display-4 text-center confirmation-title">Confirmation</h3>
      </div>

      <Container>
        {/* Main Confirmation Card */}
        <Row className="justify-content-center text-center mb-5">
          <Col md={8}>
            <Card className="confirmation-card">
              <Card.Body>
                <h2 className="card-title confirmation-title">Order Successfully Placed</h2>
                <p className="lead mb-4 confirmation-text">Thank you for choosing us!</p>
                <p className="confirmation-text">We have received your order and will process it promptly.</p>
                <p className="confirmation-text">
                  Details and delivery information will be sent to your email address:
                  <strong className="confirmation-email"> {email}</strong>.
                </p>
                <p className="confirmation-text">For any further assistance, feel free to contact our support team.</p>
                <p className="confirmation-text">
                  Once again, thank you for using <strong className="confirmation-brand">Quicket</strong>. We wish you all the best!
                </p>
                
                {/* Countdown Timer */}
                {redirectUrl && (
                  <>
                    <div className="countdown-timer" id="countdown">
                      {countdown > 0 ? `Redirecting in ${countdown} seconds...` : 'Redirecting now...'}
                    </div>
                    <div className="redirect-message">You will be redirected to event page shortly...</div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Passes Download Section */}
        {passes.length > 0 && (
          <Row className="justify-content-center text-center mb-5">
            <Col md={10}>
              <Card className="confirmation-card">
                <Card.Body>
                  <h4 className="text-center confirmation-title">Here's one-time download links for your passes!</h4>
                  <p className="text-center mb-2 confirmation-text">Download links will be valid for only 10 minutes!</p>
                  
                  <div className="table-responsive">
                    <Table className="confirmation-table">
                      <thead>
                        <tr>
                          <th scope="col">Name</th>
                          <th scope="col">Email</th>
                          <th scope="col">Expires</th>
                          <th scope="col">Download</th>
                          <th scope="col">Google Wallet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {passes.map((pass, index) => (
                          <tr key={index}>
                            <td scope="row" data-label="Name">
                              {pass.name || 'N/A'}
                            </td>
                            <td data-label="Email">
                              {pass.email || email}
                            </td>
                            <td data-label="Expires">
                              {new Date(Date.now() + 10 * 60 * 1000).toLocaleString()}
                            </td>
                            <td data-label="Download">
                              <a 
                                className="download-btn"
                                href={getPassDownloadLink(pass)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleDownloadClick}
                              >
                                <i className="fas fa-download me-1"></i>Download
                              </a>
                            </td>
                            <td data-label="Google Wallet">
                              <button 
                                onClick={(e) => handleGoogleWalletClick(pass, e)}
                                className="btn btn-link p-0"
                                style={{ border: 'none', background: 'none' }}
                              >
                                <img 
                                  src="/static/webpack_bundles/gw-btn.svg" 
                                  alt="Add to Google Wallet"
                                  className="w-100"
                                  style={{ height: '40px', objectFit: 'contain' }}
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Order Summary */}
        <Row className="justify-content-center text-center mb-5">
          <Col md={8}>
            <Card className="confirmation-card">
              <Card.Body>
                <h4 className="text-center confirmation-title">Order Summary</h4>
                <div className="order-details">
                  <Row className="mb-2">
                    <Col xs={6}>
                      <strong>Order ID:</strong>
                    </Col>
                    <Col xs={6}>
                      #{cart.id}
                    </Col>
                  </Row>
                  
                  <Row className="mb-2">
                    <Col xs={6}>
                      <strong>Payment Method:</strong>
                    </Col>
                    <Col xs={6}>
                      {cart.pgw?.name || 'N/A'}
                    </Col>
                  </Row>
                  
                  <Row className="mb-2">
                    <Col xs={6}>
                      <strong>Total Amount:</strong>
                    </Col>
                    <Col xs={6}>
                      <span className="fw-bold text-success">
                        {formatPrice(cart.total, (cart.currency && cart.currency.code) ? cart.currency.code : 'USD')}
                      </span>
                    </Col>
                  </Row>
                  
                  <Row className="mb-2">
                    <Col xs={6}>
                      <strong>Order Date:</strong>
                    </Col>
                    <Col xs={6}>
                      {new Date(cart.created_at).toLocaleDateString()}
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PaymentConfirmationPage;
